import { useState } from 'react'

import BusinessIcon from '@mui/icons-material/Business'
import EditIcon from '@mui/icons-material/Edit'
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'

import { MyListButton } from '@components/MyListButton'
import { Status } from '@components/Status'
import type { Entity } from '@models/Entity'
import {
  allowedDisciplinesForProject,
  canChangeProjectForProject,
  canCreateAssignedBijlageForProject,
  getPossibleAssigneesForProject,
  isUserVerantwoordelijkForProject,
} from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { ENTITIES } from '../../../entity'
import { Activiteiten } from '../../../entity/activiteiten/components/Activiteiten'
import { useSearchActiviteitenForEntity } from '../../../entity/activiteiten/hooks/useSearchActiviteitenForEntity'
import { AlgemeneBijlagen } from '../../../entity/bijlagen/components/AlgemeneBijlagen'
import { AssignedBijlagenByProcesstap } from '../../../entity/bijlagen/components/AssignedBijlagenByProcesstap'
import { useAssignedBijlagenForEntity } from '../../../entity/bijlagen/hooks/useAssignedBijlagenForEntity'
import { useBijlageTemplatesForEntity } from '../../../entity/bijlagen/hooks/useBijlageTemplatesForEntity'
import { useBijlagenForEntity } from '../../../entity/bijlagen/hooks/useBijlagenForEntity'
import { ContactpersonenCards } from '../../../entity/contactpersonen/components/ContactpersonenCards'
import { useContactpersonenForEntity } from '../../../entity/contactpersonen/hooks/useContactpersonenForEntity'
import { HoofdnetOpdrachten } from '../../../entity/opdrachten/components/HoofdnetOpdrachten'
import { useHoofdnetOpdrachtenForEntity } from '../../../entity/opdrachten/hooks/useHoofdnetOpdrachtenForEntity'
import { useGeblokkeerdeWekenForEntity } from '../../../entity/planning/hooks/useGeblokkeerdeWekenForEntity'
import { LinkedProjects } from '../../../entity/projecten/components/LinkedProjects'
import { useLinkedProjectsForEntity } from '../../../entity/projecten/hooks/useLinkedProjectsForEntity'
import { Subprocessen } from '../../../entity/subprocessen/components/Subprocessen'
import { useSubprocesTemplatesForEntity } from '../../../entity/subprocessen/hooks/useSubprocesTemplatesForEntity'
import { useSubprocessenForEntity } from '../../../entity/subprocessen/hooks/useSubprocessenForEntity'
import { useNavigate } from '../../../router/hooks/useNavigate'
import { useParams } from '../../../router/hooks/useParams'
import { useProjectSelectlists } from '../../../selectlists/hooks/useProjectSelectlists'
import { useStatusConfig } from '../../../status/hooks/useStatusConfig'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { useHoofdleidingValuelists } from '../../../valuelists/hooks/useHoofdleidingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { DisciplineVerzamelingen } from '../components/DisciplineVerzamelingen'
import { Log } from '../components/Log'
import { ProjectGegevensCards } from '../components/ProjectGegevensCards'
import { CreateEditProjectDialog } from '../components/dialogs/CreateEditProjectDialog'
import { ProjectOptions } from '../components/options/ProjectOptions'
import { useHerplanGeblokkeerdeWeken } from '../hooks/useHerplanGeblokkeerdeWeken'
import { useProject } from '../hooks/useProject'
import { useProjectLog } from '../hooks/useProjectLog'

import { generateTabs } from './page-util'

export const Project = () => {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  const { data: project, isError } = useProject({ id })
  const { data: log } = useProjectLog({ id })

  const user = useAuthenticatedUser()
  const [showProjectLog, setShowProjectLog] = useState(false)
  const [open, setOpen] = useState(false)

  const [anchorEl, setAnchorEl] = useState<HTMLElement>()

  const { canViewActiviteiten, organisatie } = user

  const entity: Entity = {
    id: project?.aanvraagID || '',
    type: ENTITIES.PROJECT,
    projecttype: 'Hoofdleiding',
    gebiedscode: project?.gebiedscode || '',
  }

  const entityProjects = useLinkedProjectsForEntity(entity)

  const { data: valuelists } = useValuelists()
  const { data: hoofdleidingValuelists } = useHoofdleidingValuelists()
  const { data: selectlists } = useProjectSelectlists({ id })
  const { data: statusConfig } = useStatusConfig()
  const { data: geblokkeerdeWeken } = useGeblokkeerdeWekenForEntity(entity)
  const {
    data: { items: herplanGeblokkeerdeWeken },
  } = useHerplanGeblokkeerdeWeken({
    gebiedscodes: hoofdleidingValuelists.gebieden.map((gebied) => gebied.code),
    organisatie,
  })
  const { data: activiteiten } = useSearchActiviteitenForEntity(entity)
  const { data: assignedBijlagen } = useAssignedBijlagenForEntity(entity)
  const { data: bijlagen } = useBijlagenForEntity(entity)
  const { data: bijlagetemplates } = useBijlageTemplatesForEntity(entity)
  const { data: contactpersonen } = useContactpersonenForEntity(entity)
  const { data: subprocesTemplates } = useSubprocesTemplatesForEntity(entity)
  const { data: subprocessen } = useSubprocessenForEntity(entity)
  const {
    data: opdrachten,
    onChangePage: onChangePageOpdrachten,
    isLoading: isLoadingOpdrachten,
  } = useHoofdnetOpdrachtenForEntity(entity)

  const incorrectProjectType = project?.type !== 'Hoofdleiding'

  const algemeneBijlageTemplates = bijlagetemplates.filter(
    (template) => template.scope === 'algemeen',
  )

  const hasAlgemeneBijlagen = algemeneBijlageTemplates.length > 0

  if (!project || isError || incorrectProjectType) {
    return (
      <Page
        title={['Project', 'Hoofdleiding']}
        breadcrumbs={[
          { label: 'Hoofdleiding', href: '/home', tooltip: 'CAPO home' },
          {
            label: 'Projecten',
          },
        ]}
      >
        <PageSection id="not-found" heading="Project niet gevonden" />
      </Page>
    )
  }

  const {
    aanvraagID,
    type,
    linkedProjectdossier,
    gebiedscode,
    gebiedscodeLabel,
    myList,
  } = project

  const assignees = getPossibleAssigneesForProject({ project })
  const canChangeProject = canChangeProjectForProject({ project, user })
  const canCreateAssignedBijlage = canCreateAssignedBijlageForProject({
    project,
    user,
    assignees,
  })

  const isVerantwoordelijke = isUserVerantwoordelijkForProject({
    project,
    user,
  })

  const canViewSubprocessen =
    subprocesTemplates.length > 0 || subprocessen.length > 0

  const tabs = generateTabs({
    canViewActiviteiten,
    hasAlgemeneBijlagen,
    canViewSubprocessen,
  })

  return (
    <Page
      title={[project.naam, project.aanvraagID, project.type]}
      breadcrumbs={[
        { label: 'Hoofdleiding', href: '/home', tooltip: 'CAPO home' },
        {
          label: 'Projecten',
          href: '/hoofdleiding/projecten',
          tooltip: 'Projecten overzicht',
        },
        {
          label: `${project.aanvraagID} (${project.naam})`,
        },
      ]}
      tabs={tabs}
      gap={8}
    >
      <PageSection
        id="gegevens"
        heading={project.naam}
        subheading={`Project ${aanvraagID}`}
        actions={
          <Box sx={{ marginLeft: 'auto', flexShrink: 0 }}>
            <MyListButton
              type="hoofdleiding"
              active={myList}
              aanvraagID={aanvraagID}
            />

            {linkedProjectdossier && (
              <StyledIconButton
                title={`Open projectdossier: ${linkedProjectdossier}`}
                onClick={() =>
                  navigate(`/projectdossier/${linkedProjectdossier}`)
                }
                icon={<BusinessIcon />}
              />
            )}

            <Status project={project} placement={'right'} />

            {canChangeProject && (
              <StyledIconButton
                title="Wijzig project"
                onClick={() => setOpen(true)}
                icon={<EditIcon />}
              />
            )}

            <StyledIconButton
              title={'Project log'}
              onClick={() => setShowProjectLog(!showProjectLog)}
              icon={<InfoOutlineIcon />}
            />

            <StyledIconButton
              title="Opties"
              onClick={(ev) => setAnchorEl(ev.currentTarget)}
              icon={<MoreVertIcon />}
            />
          </Box>
        }
      >
        <ProjectGegevensCards
          project={project}
          geblokkeerdeWeken={geblokkeerdeWeken}
        />
      </PageSection>

      <ContactpersonenCards contactpersonen={contactpersonen} entity={entity} />

      {canViewActiviteiten && (
        <Activiteiten
          activiteiten={activiteiten}
          entity={{
            id: aanvraagID,
            type: ENTITIES.PROJECT,
            gebiedscode,
            gebiedscodeLabel,
            projecttype: type,
          }}
          entityProps={project}
          assignees={assignees}
          isVerantwoordelijke={isVerantwoordelijke}
          geblokkeerdeWeken={herplanGeblokkeerdeWeken}
        />
      )}

      {hasAlgemeneBijlagen && (
        <AlgemeneBijlagen
          entity={entity}
          bijlagen={bijlagen}
          bijlageTemplates={bijlagetemplates}
          user={user}
        />
      )}

      <AssignedBijlagenByProcesstap
        user={user}
        entity={entity}
        projectStartUitvoeringWeek={project.startUitvoeringWeek}
        assignees={assignees}
        showCreateAssignedBijlage={canCreateAssignedBijlage}
        projectStatus={project.statusText}
        isHoofdleidingProject
        isVerantwoordelijke={isVerantwoordelijke}
        disciplineVerzamelingen={allowedDisciplinesForProject(project)}
        assignedBijlagen={assignedBijlagen}
        bijlagen={bijlagen}
        bijlageTemplates={bijlagetemplates}
        geblokkeerdeWeken={herplanGeblokkeerdeWeken}
      />

      {canViewSubprocessen && (
        <Subprocessen
          project={project}
          entity={entity}
          subprocessen={subprocessen}
          aannemers={project.aannemers || []}
          user={user}
          templates={subprocesTemplates}
          bijlageTemplates={bijlagetemplates}
          assignedBijlagen={assignedBijlagen}
          bijlagen={bijlagen}
        />
      )}

      <DisciplineVerzamelingen project={project} />

      <HoofdnetOpdrachten
        opdrachten={opdrachten}
        onChangePage={onChangePageOpdrachten}
        isLoading={isLoadingOpdrachten}
      />

      <LinkedProjects
        user={user}
        parent={entity}
        projects={entityProjects.data.items}
        projectGroups={entityProjects.data.groups}
        isLoading={entityProjects.isFetching}
        isError={entityProjects.isError}
      />

      <Log
        open={showProjectLog}
        onClose={() => setShowProjectLog(false)}
        log={log}
        gebieden={valuelists.gebieden}
      />

      <ProjectOptions
        project={project}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        processtappen={statusConfig.processtappenHoofdleiding}
        geblokkeerdeWeken={herplanGeblokkeerdeWeken}
        selectableLabels={selectlists.labels}
      />

      {open && (
        <CreateEditProjectDialog
          projectInfo={project}
          handleClose={() => setOpen(false)}
          type="edit"
          geblokkeerdeWeken={herplanGeblokkeerdeWeken}
          bijlagetemplates={bijlagetemplates}
        />
      )}
    </Page>
  )
}
